// Lecturas de «Cobros» contra el silo de crédito. Nunca lanzan: cada desenlace vuelve tipado para
// que la pantalla distinga «sin permiso» de «no se pudo leer».
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import {
    getCredit,
    getSellerCredit,
    listCreditInstallments,
    listSellerCredits,
    lookupSellerCredit,
    type LoanCreditDetail,
    type LoanPaymentListItem,
    type SellerCreditDetailResponse,
    type SellerCreditListResponse,
    type SellerCreditLookupResult,
    type SellerCreditRow,
} from '@/api/services/loan-credit';
import { ApiError } from '@/api/errors';
import { creditReadStateOf } from '../../customers/api/creditPortfolioLoader';
import type { CollectionCredit, CreditRead } from '../lib/collectionsModel';

export interface CollectionsDeps {
    getCredit: (creditId: string) => Promise<LoanCreditDetail>;
    listCreditInstallments: (creditId: string) => Promise<LoanInstallmentResponse[]>;
    lookupSellerCredit: (identifier: string) => Promise<SellerCreditLookupResult>;
    listSellerCredits: () => Promise<SellerCreditListResponse>;
    getSellerCredit: (creditId: string) => Promise<SellerCreditDetailResponse>;
}

export const DEFAULT_COLLECTIONS_DEPS: CollectionsDeps = {
    getCredit,
    listCreditInstallments,
    lookupSellerCredit,
    listSellerCredits: () => listSellerCredits(),
    getSellerCredit,
};

/** El crédito con su plan. */
export async function loadCreditRead(creditId: string, deps: CollectionsDeps): Promise<CreditRead> {
    try {
        const [credit, installments] = await Promise.all([
            deps.getCredit(creditId),
            deps.listCreditInstallments(creditId),
        ]);
        return { state: 'ready', snapshot: { credit, installments } };
    } catch (error) {
        return { state: creditReadStateOf(error) };
    }
}

/** Una fila de mi cartera tal como la resume loan-credit. El detalle se lee al abrirla. */
export function sellerCreditRowOf(item: SellerCreditRow): CollectionCredit {
    const context = item.saleContext;
    return {
        creditId: item.creditId,
        saleId: context.saleId ?? null,
        customerName: context.customerName ?? '—',
        customerCurp: null,
        product: [context.productBrand, context.productModel].filter(Boolean).join(' '),
        imei: item.imei ?? null,
        isMine: true,
        summary: item,
        read: { state: 'loading' },
    };
}

/** Lo que trae el detalle de un crédito propio: la lectura del crédito y sus últimos cobros. */
export interface SellerCreditLoad {
    read: CreditRead;
    recentPayments: LoanPaymentListItem[];
}

/** Detalle, plan y cobros recientes de un crédito propio en UNA llamada. Un 404 (ajeno) es «no se pudo leer». */
export async function loadSellerCredit(
    creditId: string,
    deps: CollectionsDeps,
): Promise<SellerCreditLoad> {
    try {
        const { credit, installments, recentPayments } = await deps.getSellerCredit(creditId);
        return { read: { state: 'ready', snapshot: { credit, installments } }, recentPayments };
    } catch (error) {
        return { read: { state: creditReadStateOf(error) }, recentPayments: [] };
    }
}

export type WalkInResult =
    | { state: 'ready'; rows: CollectionCredit[] }
    | { state: 'forbidden' }
    | { state: 'error' };

/** «No existe», «de otra cadena» e «identificador que no es de ninguna forma» son «sin resultados». */
const NO_MATCH_CODES: ReadonlySet<string> = new Set([
    'CREDIT_NOT_FOUND',
    'INVALID_LOOKUP_IDENTIFIER',
]);

function walkInRowOf(lookup: SellerCreditLookupResult): CollectionCredit {
    return {
        creditId: lookup.creditId,
        saleId: null,
        customerName: lookup.customerName ?? '—',
        customerCurp: null,
        product: [lookup.productBrand, lookup.productModel].filter(Boolean).join(' '),
        imei: null,
        isMine: false,
        summary: null,
        read: { state: 'walk-in', lookup },
    };
}

/** Busca fuera de la cartera con la búsqueda de la cadena. Un crédito que ya es mío no se repite. */
export async function loadWalkIn(
    term: string,
    mine: Set<string>,
    deps: CollectionsDeps,
): Promise<WalkInResult> {
    try {
        const lookup = await deps.lookupSellerCredit(term.replace(/[\s-]/g, ''));
        const rows = mine.has(lookup.creditId) ? [] : [walkInRowOf(lookup)];
        return { state: 'ready', rows };
    } catch (error) {
        if (error instanceof ApiError && NO_MATCH_CODES.has(error.code)) {
            return { state: 'ready', rows: [] };
        }
        return { state: creditReadStateOf(error) };
    }
}
